import React from 'react';
import { useAuth } from '../auth/AuthContext';

export const ApplicationRoles = {
  USER: 'User',
  JOB_SEEKER: 'JobSeeker',
  COMPANY: 'Company',
  COMPANY_OWNER: 'CompanyOwner',
  ADMIN: 'Admin',
} as const;
type ApplicationRole = (typeof ApplicationRoles)[keyof typeof ApplicationRoles];

export const ApplicationRolesGeneral = [
  ApplicationRoles.USER,
  ApplicationRoles.JOB_SEEKER,
];

export const ApplicationRolesCompany = [
  ApplicationRoles.COMPANY,
  ApplicationRoles.COMPANY_OWNER,
];

export const ApplicationRolesAdminArea = [
  ApplicationRoles.ADMIN,
];

export const ApplicationPermissions = {
  CAN_ACCESS_ADMIN_VIEW: 'canAccessAdminView',
  CAN_ACCESS_USER_VIEW: 'canAccessUserView',
  CAN_ACCESS_COMPANY_VIEW: 'canAccessCompanyView',
  CAN_ACCESS_EXPERT_VIEW: 'canAccessExpertView',
  CAN_ACCESS_FEED: 'canAccessFeed',
  CAN_POST_JOBS: 'canPostJobs',
  CAN_APPLY_FOR_JOBS: 'canApplyForJobs',
  CAN_MANAGE_APPLICATIONS: 'canManageApplications',
  CAN_MODERATE_CONTENT: 'canModerateContent',
  CAN_MANAGE_USERS: 'canManageUsers',
  CAN_VIEW_ANALYTICS: 'canViewAnalytics',
  CAN_ACCESS_PREMIUM_FEATURES: 'canAccessPremiumFeatures',
} as const;
type ApplicationPermission = (typeof ApplicationPermissions)[keyof typeof ApplicationPermissions];

const AppPermissionToRoles: { [key in ApplicationPermission]: Array<string> } = {
  [ApplicationPermissions.CAN_ACCESS_ADMIN_VIEW]: [ApplicationRoles.ADMIN],
  [ApplicationPermissions.CAN_ACCESS_USER_VIEW]: [ApplicationRoles.USER, ApplicationRoles.JOB_SEEKER],
  [ApplicationPermissions.CAN_ACCESS_COMPANY_VIEW]: [ApplicationRoles.COMPANY, ApplicationRoles.COMPANY_OWNER],
  [ApplicationPermissions.CAN_ACCESS_EXPERT_VIEW]: [ApplicationRoles.ADMIN], // Experts are typically admins or have special roles
  [ApplicationPermissions.CAN_ACCESS_FEED]: [
    ApplicationRoles.USER,
    ApplicationRoles.JOB_SEEKER,
    ApplicationRoles.COMPANY,
    ApplicationRoles.COMPANY_OWNER,
    ApplicationRoles.ADMIN,
  ],
  [ApplicationPermissions.CAN_POST_JOBS]: [
    ApplicationRoles.COMPANY,
    ApplicationRoles.COMPANY_OWNER,
    ApplicationRoles.ADMIN,
  ],
  [ApplicationPermissions.CAN_APPLY_FOR_JOBS]: [
    ApplicationRoles.USER,
    ApplicationRoles.JOB_SEEKER,
    ApplicationRoles.ADMIN,
  ],
  [ApplicationPermissions.CAN_MANAGE_APPLICATIONS]: [
    ApplicationRoles.COMPANY,
    ApplicationRoles.COMPANY_OWNER,
    ApplicationRoles.ADMIN,
  ],
  [ApplicationPermissions.CAN_MODERATE_CONTENT]: [
    ApplicationRoles.ADMIN,
  ],
  [ApplicationPermissions.CAN_MANAGE_USERS]: [
    ApplicationRoles.ADMIN,
  ],
  [ApplicationPermissions.CAN_VIEW_ANALYTICS]: [
    ApplicationRoles.ADMIN,
  ],
  [ApplicationPermissions.CAN_ACCESS_PREMIUM_FEATURES]: [
    ApplicationRoles.COMPANY,
    ApplicationRoles.COMPANY_OWNER,
    ApplicationRoles.ADMIN,
  ],
};

interface User {
  id: string;
  email?: string;
  applicationRoles?: string[];
  expertRoles?: string[];
  [key: string]: any;
}

function usePermissions(user: User | null) {
  const { applicationRoles: userApplicationRoles = [] } = user || {};

  // Use application roles for permission checking, with fallback to account_type
  let allUserRoles = [...userApplicationRoles];
  
  // If no application roles are found, try to derive from account_type
  if (allUserRoles.length === 0 && user?.account_type) {
    switch (user.account_type) {
      case 'CompanyOwner':
        allUserRoles = [ApplicationRoles.COMPANY_OWNER];
        break;
      case 'Company':
        allUserRoles = [ApplicationRoles.COMPANY];
        break;
      case 'JobSeeker':
        allUserRoles = [ApplicationRoles.JOB_SEEKER];
        break;
      default:
        allUserRoles = [ApplicationRoles.USER];
    }
  }

  function hasAppPermission(key: ApplicationPermission): boolean {
    if (!user || allUserRoles.length === 0) {
      return false;
    }

    return allUserRoles.some((role: string) => 
      AppPermissionToRoles[key].includes(role)
    );
  }

  function isAllowedTo(key: ApplicationPermission, role?: string): boolean {
    if (role === undefined || !user) {
      return false;
    }

    return AppPermissionToRoles[key].includes(role);
  }

  function hasAnyRole(roles: string[]): boolean {
    if (!user || allUserRoles.length === 0) {
      return false;
    }

    return allUserRoles.some((userRole: string) => roles.includes(userRole));
  }

  function hasAllRoles(roles: string[]): boolean {
    if (!user || allUserRoles.length === 0) {
      return false;
    }

    return roles.every((role: string) => allUserRoles.includes(role));
  }

  function isAdmin(): boolean {
    return hasAppPermission(ApplicationPermissions.CAN_ACCESS_ADMIN_VIEW);
  }

  function isCompany(): boolean {
    return hasAppPermission(ApplicationPermissions.CAN_ACCESS_COMPANY_VIEW);
  }

  function isUser(): boolean {
    return hasAppPermission(ApplicationPermissions.CAN_ACCESS_USER_VIEW);
  }

  function isExpert(): boolean {
    if (!user || !user.expertRoles || user.expertRoles.length === 0) {
      return false;
    }
    return user.expertRoles.length > 0;
  }

  function getPrimaryRole(): ApplicationRole {
    if (!user || !userApplicationRoles || userApplicationRoles.length === 0) {
      return ApplicationRoles.USER;
    }
    return userApplicationRoles[0] as ApplicationRole;
  }

  function getAccountType(): string {
    return user?.account_type || 'JobSeeker';
  }

  function getRoleDisplay(): string {
    const primaryRole = getPrimaryRole();
    const accountType = getAccountType();
    
    if (accountType === 'CompanyOwner') {
      return 'Company Owner';
    } else if (accountType === 'Company') {
      return 'Company';
    } else if (accountType === 'JobSeeker') {
      return 'Job Seeker';
    }
    
    return primaryRole;
  }

  function getRoleBadgeColor(): string {
    const accountType = getAccountType();
    
    switch (accountType) {
      case 'Admin':
        return 'red';
      case 'CompanyOwner':
        return 'purple';
      case 'Company':
        return 'purple';
      case 'JobSeeker':
        return 'blue';
      default:
        return 'gray';
    }
  }

  function getExpertTypes(): string[] {
    if (!user || !user.expertRoles || user.expertRoles.length === 0) {
      return [];
    }
    return user.expertRoles;
  }

  return {
    ApplicationRoles,
    ApplicationPermissions,
    hasAppPermission,
    isAllowedTo,
    hasAnyRole,
    hasAllRoles,
    isAdmin,
    isCompany,
    isUser,
    isExpert,
    getPrimaryRole,
    getAccountType,
    getRoleDisplay,
    getRoleBadgeColor,
    getExpertTypes,
    userRoles: allUserRoles,
  };
}

type UsePermissionsReturn = ReturnType<typeof usePermissions>;

const Context = React.createContext<UsePermissionsReturn | undefined>(undefined);
Context.displayName = 'PermissionsContext';

export const useContext = () => {
  const context = React.useContext(Context);

  if (!context) {
    const error = new Error(`[${Context.displayName}] is null or undefined`);
    error.name = 'ContextError';
    // Guard for environments where captureStackTrace is not present in lib typings
    if (typeof (Error as any).captureStackTrace === 'function') {
      (Error as any).captureStackTrace(error, useContext);
    }
    throw error;
  }

  return context;
};

export const Provider: React.FC<{ children: React.ReactNode }> = (props) => {
  const { user } = useAuth();
  const ctx = usePermissions(user);
  const context = React.useMemo(() => ctx, [ctx]);

  return <Context.Provider value={context}>{props.children}</Context.Provider>;
};
